import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { fmtCVE, fmtDate, INVOICE_STATUS, DOC_TYPES } from "@/lib/format";
import { DocItemsEditor } from "@/components/DocItemsEditor";
import { NoFiscalNotice } from "@/components/NoFiscalNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Plus, DotsThreeVertical, Receipt, CircleNotch, FilePdf, PencilSimple, Trash, Eye, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["rascunho", "emitida", "enviada", "paga", "parcialmente_paga", "vencida", "anulada"];

export default function Invoices() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ client_id: "", doc_type: "proforma", series: "A", due_date: "", payment_terms: "30 dias", notes: "", items: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, c, p] = await Promise.all([
        api.get("/invoices", { params: { status: filter || undefined } }),
        api.get("/clients", { params: { limit: 200 } }),
        api.get("/products", { params: { limit: 200, active: true } }),
      ]);
      setItems(inv.data.items); setClients(c.data.items); setProducts(p.data.items);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ client_id: "", doc_type: "proforma", series: "A", due_date: "", payment_terms: "30 dias", notes: "", items: [] });
    setOpen(true);
  };
  const openEdit = async (inv) => {
    const { data } = await api.get(`/invoices/${inv.id}`);
    setEditing(data);
    setForm({ client_id: data.client_id, doc_type: data.doc_type, series: data.series,
      due_date: data.due_date || "", payment_terms: data.payment_terms || "", notes: data.notes || "", items: data.items || [] });
    setOpen(true);
  };

  const save = async () => {
    if (!form.client_id) return toast.error("Selecione um cliente");
    if (form.items.length === 0) return toast.error("Adicione pelo menos uma linha");
    setBusy(true);
    try {
      const payload = { ...form, due_date: form.due_date || null };
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editing ? "Documento atualizado" : "Documento criado");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const setStatus = async (inv, status) => {
    try { await api.patch(`/invoices/${inv.id}/status`, { status }); toast.success("Estado atualizado"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (inv) => {
    if (!window.confirm(`Eliminar ${inv.number}?`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Eliminada"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Vendas</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Pró-formas e recibos</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="invoice-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os estados</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{INVOICE_STATUS[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="rounded-full" onClick={openNew} data-testid="new-invoice-btn">
            <Plus size={18} className="mr-1.5" /> Novo documento
          </Button>
        </div>
      </div>

      <NoFiscalNotice />

      <div className="rounded-xl border border-border bg-card">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <Receipt size={40} className="text-muted-foreground" />
            <p className="text-muted-foreground">Ainda não há documentos de venda.</p>
            <Button variant="outline" className="rounded-full" onClick={openNew}>Criar primeiro documento</Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Número</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="hidden lg:table-cell">Tipo</TableHead>
                <TableHead className="hidden xl:table-cell">Vencimento</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="hidden xl:table-cell text-right">Saldo</TableHead>
                <TableHead className="hidden sm:table-cell">Estado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((inv) => {
                const st = INVOICE_STATUS[inv.status] || INVOICE_STATUS.rascunho;
                return (
                  <TableRow key={inv.id} className="stagger-item cursor-pointer" data-testid={`invoice-row-${inv.id}`}
                    onClick={() => navigate(`/vendas/${inv.id}`)}>
                    <TableCell className="font-mono text-xs font-medium">{inv.number}</TableCell>
                    <TableCell>{inv.client_name}</TableCell>
                    <TableCell className="hidden lg:table-cell text-muted-foreground">{DOC_TYPES[inv.doc_type]}</TableCell>
                    <TableCell className="hidden xl:table-cell text-muted-foreground">{fmtDate(inv.due_date)}</TableCell>
                    <TableCell className="text-right font-medium">{fmtCVE(inv.totals?.total)}</TableCell>
                    <TableCell className="hidden xl:table-cell text-right text-muted-foreground">{fmtCVE(inv.balance)}</TableCell>
                    <TableCell className="hidden sm:table-cell"><Badge variant="outline" className={st.cls}>{st.label}</Badge></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`invoice-actions-${inv.id}`}>
                            <DotsThreeVertical size={20} weight="bold" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => navigate(`/vendas/${inv.id}`)}><Eye size={15} className="mr-2" /> Ver detalhe</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEdit(inv)}><PencilSimple size={15} className="mr-2" /> Editar</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`${API}/invoices/${inv.id}/pdf`, "_blank")} data-testid={`invoice-pdf-${inv.id}`}>
                            <FilePdf size={15} className="mr-2" /> Ver PDF
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs">Alterar estado</DropdownMenuLabel>
                          {["emitida", "enviada", "anulada"].map((s) => (
                            <DropdownMenuItem key={s} onClick={() => setStatus(inv, s)}>
                              <PaperPlaneTilt size={15} className="mr-2" /> {INVOICE_STATUS[s].label}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => remove(inv)}>
                            <Trash size={15} className="mr-2" /> Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle>{editing ? `Editar ${editing.number}` : "Novo documento de venda"}</DialogTitle></DialogHeader>
          <NoFiscalNotice />
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Cliente *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="invoice-client"><SelectValue placeholder="Selecionar cliente" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo de documento</Label>
                <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                  <SelectTrigger data-testid="invoice-doctype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(DOC_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Série</Label>
                <Input value={form.series} onChange={(e) => setForm({ ...form, series: e.target.value })} data-testid="invoice-series" />
              </div>
              <div className="space-y-1.5">
                <Label>Vencimento</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due" />
              </div>
            </div>
            <DocItemsEditor items={form.items} setItems={(items) => setForm({ ...form, items })} products={products} />
            <div className="space-y-1.5">
              <Label>Condições de pagamento</Label>
              <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} data-testid="invoice-terms" />
            </div>
            <div className="space-y-1.5">
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="invoice-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="save-invoice-btn">
              {busy ? <CircleNotch className="animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
